import { WaqLink } from "../../../ui/waq/WaqLink";
import { CountUpStat } from "../../../ui/waq/CountUpStat";
import { GlowBlob } from "../../../ui/waq/GlowBlob";
import {
  caseStudiesSection,
  featuredStudy,
  moreEngagements,
  statCards,
  testimonial,
} from "../../../../content/waq/caseStudies";

/** design/Homepage.dc.html lines 219-271. Last section of the dark run. */
export function CaseStudies() {
  return (
    <section className="relative" aria-labelledby="case-studies-heading">
      <GlowBlob
        position={{ top: -260, left: -160 }}
        size={780}
        alpha={0.34}
        falloff="64%"
        animated
        duration="slow"
      />

      <div className="relative mx-auto max-w-container px-section-x py-section-y">
        <div className="mb-section-head-alt flex flex-wrap items-end justify-between gap-6 motion-safe:animate-rise">
          <div>
            <p className="mb-3.5 text-eyebrow font-extrabold uppercase text-yellow">
              {caseStudiesSection.eyebrow}
            </p>
            <h2
              id="case-studies-heading"
              className="max-w-[760px] text-h2 font-extrabold"
            >
              {caseStudiesSection.heading}
            </h2>
          </div>
          <WaqLink
            href={caseStudiesSection.allLink.href}
            className="border-b-[3px] border-yellow pb-[3px] text-nav font-extrabold text-surface hover:text-surface"
          >
            {caseStudiesSection.allLink.label}
          </WaqLink>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] items-start gap-cases pb-cases-pb motion-safe:animate-rise">
          <div>
            <p className="mb-[18px] inline-flex items-center gap-[9px] rounded-pill bg-yellow px-3.5 py-[7px] text-status font-extrabold uppercase text-navy">
              {featuredStudy.badge}
            </p>

            <h3 className="mb-6 text-case-title font-extrabold">
              {featuredStudy.heading}
            </h3>

            <div className="grid max-w-[600px] gap-5">
              {featuredStudy.blocks.map((block) => (
                <div
                  key={block.label}
                  className={`border-l-[3px] pl-5 ${
                    block.tone === "yellow" ? "border-yellow" : "border-blue/50"
                  }`}
                >
                  <p
                    className={`mb-1.5 text-block-label font-extrabold uppercase ${
                      block.tone === "yellow" ? "text-yellow" : "text-blue"
                    }`}
                  >
                    {block.label}
                  </p>
                  <p className="text-body-tight text-ondark-body">{block.body}</p>
                </div>
              ))}
            </div>

            {/* The reference has a "Read the full study" button here. Removed at
                the client's request: the featured 2.5x engagement has no detail
                page, so it was the homepage's most prominent dead link — a
                yellow primary button that did nothing. The section still routes
                to /case-studies through "All case studies" in its header. */}
          </div>

          <div className="grid gap-4">
            {statCards.map((card) => (
              <div
                key={card.label}
                className={`rounded-feature border-l-[6px] bg-ondark-card p-7 ${
                  card.accent === "yellow" ? "border-yellow" : "border-blue"
                }`}
              >
                <CountUpStat
                  to={card.to}
                  suffix={card.suffix}
                  className={`block text-stat font-extrabold tabular-nums ${
                    card.valueIsYellow ? "text-yellow" : ""
                  }`}
                />
                <p className="mt-3.5 text-stat-label font-extrabold">{card.label}</p>
                <p className="mt-1 text-meta text-white/65">{card.detail}</p>
              </div>
            ))}

            <figure className="rounded-feature bg-blue/[.12] p-[26px]">
              <blockquote className="mb-3.5 text-quote font-semibold">
                {testimonial.quote}
              </blockquote>
              <figcaption className="text-caption-sm text-ondark-soft">
                <strong className="font-extrabold text-surface">
                  {testimonial.attribution}
                </strong>
                <br />
                {testimonial.source}
              </figcaption>
            </figure>
          </div>
        </div>

        <div className="motion-safe:animate-rise">
          <p className="mb-1.5 text-label-wide font-extrabold uppercase text-white/50">
            {moreEngagements.label}
          </p>
          {moreEngagements.rows.map((row) => (
            <div
              key={row.title}
              className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)_auto] items-center gap-rows border-t border-ondark-border py-[22px]"
            >
              <p className="text-micro-lg font-extrabold uppercase text-yellow">
                {row.industry}
              </p>
              <div>
                <p className="text-row-title font-bold">{row.title}</p>
                <p className="mt-1 text-meta text-white/60">{row.detail}</p>
              </div>
              {/* The reference leaves this column as a "you supply" slot,
                  styled for a short uppercase tag. It carries the engagement
                  shape — duration, team size, region — the same vocabulary
                  /industries/pharma §7 uses for the same kind of row. */}
              <p className="whitespace-nowrap text-status font-extrabold uppercase text-white/60">
                {row.shape}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
